import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchQuizQuestions, submitQuiz } from "@/lib/api";
import Seo from "@/components/Seo";
import { ChevronLeft, ChevronRight, Sparkles, RotateCcw } from "lucide-react";

const SCALE = [
    { value: 1, label: "Helt uenig" },
    { value: 2, label: "Uenig" },
    { value: 3, label: "Neutral" },
    { value: 4, label: "Enig" },
    { value: 5, label: "Helt enig" },
];

export default function Quiz() {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchQuizQuestions().then(setQuestions);
    }, []);

    const current = questions[step];
    const isLast = step === questions.length - 1;
    const progress = questions.length ? ((step + 1) / questions.length) * 100 : 0;

    const choose = (v) => {
        setAnswers((a) => ({ ...a, [current.id]: v }));
    };

    const next = () => {
        if (isLast) {
            submit();
        } else {
            setStep((s) => s + 1);
        }
    };

    const prev = () => setStep((s) => Math.max(0, s - 1));

    const submit = async () => {
        setLoading(true);
        try {
            const payload = Object.entries(answers).map(([question_id, score]) => ({ question_id, score }));
            const res = await submitQuiz(payload);
            setResults(res);
        } catch (e) {
            alert(e.response?.data?.detail || "Fejl i afsendelsen");
        } finally {
            setLoading(false);
        }
    };

    const restart = () => {
        setAnswers({});
        setStep(0);
        setResults(null);
    };

    if (results) {
        return <QuizResults results={results} onRestart={restart} />;
    }

    if (!current) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-20 text-center text-[#4F5A7A]">
                Indlæser quiz...
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="quiz-page">
            <Seo
                title="Vælger-quizzen — find dit parti"
                description="Tag vores gratis vælger-quiz med 18 politiske spørgsmål og find ud af, hvilket dansk parti dine holdninger matcher bedst. Klima, udlændinge, økonomi, velfærd og forsvar."
                keywords="vælgerquiz, vælger-quiz, find parti, dansk politisk quiz, klima, udlændinge, økonomi, velfærd, Folketinget"
                path="/quiz"
            />
            <div className="mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-royal-soft text-royal text-xs uppercase tracking-[0.18em] font-semibold mb-4">
                    <Sparkles className="w-3.5 h-3.5" />
                    Vælger-quiz
                </div>
                <h1 className="font-serif-dm text-5xl text-royal leading-tight">
                    Hvilket parti passer til dig?
                </h1>
                <p className="text-[#4F5A7A] mt-3 max-w-2xl">
                    Svar på {questions.length} spørgsmål om dansk politik — så finder vi
                    det parti, der ligger tættest på dine holdninger. Dit svar deles ikke
                    med andre.
                </p>
            </div>

            {/* Progress */}
            <div className="mb-8">
                <div className="flex justify-between text-xs text-[#4F5A7A] font-semibold mb-2">
                    <span>Spørgsmål {step + 1} / {questions.length}</span>
                    <span>{Object.keys(answers).length} / {questions.length} besvaret</span>
                </div>
                <div className="w-full h-2 bg-[#EEF0F7] rounded-full overflow-hidden">
                    <div
                        className="h-full bg-royal transition-all duration-300"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Spørgsmål */}
            <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-10 mb-6" data-testid={`question-${current.id}`}>
                <span
                    className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-royal-soft text-royal mb-4"
                    data-testid="question-category"
                >
                    {current.category}
                </span>
                <h2 className="font-serif-dm text-2xl md:text-3xl text-royal leading-tight mb-8">
                    {current.text}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    {SCALE.map((s) => (
                        <button
                            key={s.value}
                            onClick={() => choose(s.value)}
                            data-testid={`answer-${current.id}-${s.value}`}
                            className={`py-4 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                                answers[current.id] === s.value
                                    ? "border-royal bg-royal text-white"
                                    : "border-[#E5E8F0] bg-white text-[#0B1A3A] hover:border-royal/40 hover:bg-royal-soft"
                            }`}
                        >
                            <div className="font-serif-dm text-2xl mb-1">{s.value}</div>
                            <div className="text-[11px] leading-tight">{s.label}</div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex justify-between items-center">
                <button
                    onClick={prev}
                    disabled={step === 0}
                    data-testid="quiz-prev"
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-royal disabled:opacity-30"
                >
                    <ChevronLeft className="w-4 h-4" /> Tilbage
                </button>
                <button
                    onClick={next}
                    disabled={!answers[current.id] || loading}
                    data-testid="quiz-next"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-royal text-white rounded-full font-semibold hover:bg-royal-deep disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {isLast ? (loading ? "Beregner..." : "Se resultatet") : "Næste"}
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function QuizResults({ results, onRestart }) {
    const top = results.results[0];
    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="quiz-results">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-royal-soft text-royal text-xs uppercase tracking-[0.18em] font-semibold mb-4">
                    <Sparkles className="w-3.5 h-3.5" />
                    Dit resultat
                </div>
                <h1 className="font-serif-dm text-4xl md:text-5xl text-royal leading-tight">
                    Dit bedste match er
                </h1>
            </div>

            {/* Top match */}
            <div
                className="relative rounded-3xl p-8 md:p-12 text-white mb-6 overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${top.color} 0%, #061845 100%)` }}
            >
                <div className="grid md:grid-cols-12 gap-6 items-center relative">
                    <div className="md:col-span-3 flex justify-center md:justify-start">
                        <div
                            className="w-28 h-28 rounded-xl bg-white flex items-center justify-center font-serif-dm text-6xl font-bold"
                            style={{ color: top.color }}
                        >
                            {top.short_name}
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <h2 className="font-serif-dm text-4xl md:text-5xl mb-1">{top.party_name}</h2>
                        <p className="text-white/75">{top.ideology} · Leder: {top.leader}</p>
                    </div>
                    <div className="md:col-span-3 text-right">
                        <div className="font-serif-dm text-7xl leading-none">{top.match}%</div>
                        <div className="text-xs uppercase tracking-wider text-white/70 mt-1">Match</div>
                    </div>
                </div>
            </div>

            {/* Alle resultater */}
            <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8 mb-6">
                <h3 className="font-serif-dm text-2xl text-royal mb-5">Alle partier efter match</h3>
                <div className="space-y-2">
                    {results.results.map((r, i) => (
                        <div
                            key={r.party_id}
                            className="flex items-center gap-4 py-2"
                            data-testid={`result-${r.party_id}`}
                        >
                            <div className="w-6 text-center font-serif-dm text-lg text-[#4F5A7A]">{i + 1}</div>
                            <Link
                                to={`/partier/${r.party_id}`}
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0 hover:scale-105 transition-transform"
                                style={{ backgroundColor: r.color }}
                            >
                                {r.short_name}
                            </Link>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-3 mb-1">
                                    <Link
                                        to={`/partier/${r.party_id}`}
                                        className="font-semibold text-royal hover:text-danish-red transition-colors truncate"
                                    >
                                        {r.party_name}
                                    </Link>
                                    <span className="text-sm font-semibold text-[#0B1A3A]">{r.match}%</span>
                                </div>
                                <div className="w-full h-2 bg-[#EEF0F7] rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{ width: `${Math.max(2, r.match)}%`, backgroundColor: r.color }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                    onClick={onRestart}
                    data-testid="quiz-restart"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-[#E5E8F0] text-royal rounded-full font-semibold hover:bg-royal-soft"
                >
                    <RotateCcw className="w-4 h-4" /> Tag quizzen igen
                </button>
                <Link
                    to={`/partier/${top.party_id}`}
                    data-testid="quiz-view-party"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-royal text-white rounded-full font-semibold hover:bg-royal-deep"
                >
                    Se {top.party_name}s profil <ChevronRight className="w-4 h-4" />
                </Link>
            </div>

            <p className="text-center text-xs text-[#8A94AE] italic mt-8 max-w-2xl mx-auto">
                Resultatet er baseret på {results.total_questions} spørgsmål og partiernes
                officielle positioner. Ingen algoritme kan erstatte selvstændig tænkning —
                tjek altid selv partiernes mærkesager og løftehistorik.
            </p>
        </div>
    );
}
